/** Additive frozen NormalMonitoringForecastV1. Backend supplies every numeric result. */
export type ForecastMetric = 'TEMPERATURE' | 'PARTICULATE';
export interface ForecastSample {
  reference: string;
  observedAt: string;
  receivedAt: string;
  availableAt: string;
  availableSequence: number;
  value: number;
  instanceId: string;
  areaId: string;
  sourceId: string;
  metric: string;
  unit: string;
  epoch: number;
  quality: string;
  unknowns: string[];
}
export interface ForecastPoint {
  horizonSeconds: number;
  targetAt: string;
  deadlineAt: string;
  localLinearValue: number;
  lastValue: number;
  threshold: number;
  localLinearCrosses: boolean;
  lastValueCrosses: boolean;
}
export interface Forecast {
  forecastId: string;
  instanceId: string;
  areaId: string;
  sourceId: string;
  metric: ForecastMetric;
  unit: string;
  epoch: number;
  configRevision: number;
  sourceRevision: number;
  origin: string;
  issuedAt: string | null;
  persistedAtUpperBound: string | null;
  inputWatermark: number;
  inputFingerprint: string;
  profileVersion: string;
  algorithmVersion: string;
  benchmarkVersion: string;
  mode: string;
  publishStatus: string;
  sampleCount: number;
  spanSeconds: number;
  intercept: number | null;
  slopePerSecond: number | null;
  latestValue: number | null;
  inputs: ForecastSample[];
  points: ForecastPoint[];
  limitations: string[];
}
export interface ForecastEvaluation {
  forecastId: string;
  horizonSeconds: number;
  targetAt: string;
  deadlineAt: string;
  evaluatedAt: string | null;
  status: string;
  reason: string;
  candidates: ForecastSample[];
  actualRef: string | null;
  actualObservedAt: string | null;
  actualReceivedAt: string | null;
  actualAvailableAt: string | null;
  actualValue: number | null;
  linearSignedError: number | null;
  linearAbsoluteError: number | null;
  linearSquaredError: number | null;
  lastSignedError: number | null;
  lastAbsoluteError: number | null;
  lastSquaredError: number | null;
  linearOutcome: string | null;
  lastOutcome: string | null;
}
export interface ForecastDetail {
  forecast: Forecast;
  applicability: { forecastId: string; status: string; reason: string; changedAt: string | null };
  evaluations: ForecastEvaluation[];
}
export interface ForecastQualification {
  sourceId: string;
  areaId: string;
  metric: string;
  unit: string;
  epoch: number;
  enabled: boolean;
  status: string;
  reason: string;
  sampleCount: number;
  spanSeconds: number;
  latestObservedAt: string | null;
  inputWatermark: number;
  latestForecastId: string | null;
}
export interface ForecastWatch {
  watchId: string;
  areaId: string;
  horizonSeconds: number;
  origin: string;
  targetAt: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  forecastIds: string[];
  sourceIds: string[];
  reason: string;
  activeObservedWarningId: string | null;
  limitations: string[];
}
export interface ForecastSummary {
  profileVersion: string;
  generatedAt: string;
  eligibleSourceCount: number;
  forecastCount: number;
  forecastTaskCount: number;
  pendingForecastTaskCount: number;
  pendingEvaluationCount: number;
  evaluatedCount: number;
  unavailableCount: number;
  activeWatchCount: number;
  sources: ForecastQualification[];
  watches: ForecastWatch[];
  limitations: string[];
}
export interface ForecastPage {
  items: Forecast[];
  total: number;
  offset: number;
  limit: number;
  generatedAt: string;
}
export interface ForecastUtilityGroup {
  metric: ForecastMetric;
  unit: string;
  horizonSeconds: number;
  algorithmVersion: string;
  profileVersion: string;
  generated: number;
  timely: number;
  evaluable: number;
  unavailable: number;
  invalidated: number;
  mae: number | null;
  rmse: number | null;
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  precision: number | null;
  recall: number | null;
}
export interface ForecastUtility {
  generatedAt: string;
  predictiveUtility: string;
  groups: ForecastUtilityGroup[];
  limitations: string[];
}
export interface ForecastFilter {
  areaId: string;
  sourceId: string;
  metric: '' | ForecastMetric;
  unit: string;
  from: string;
  to: string;
  offset: number;
  limit: number;
}
export interface ForecastUtilityFilter {
  metric?: ForecastMetric;
  horizonSeconds?: number;
  profileVersion?: string;
  algorithmVersion?: string;
  from?: string;
  to?: string;
}
export const INITIAL_FORECAST_FILTER: ForecastFilter = {
  areaId: '',
  sourceId: '',
  metric: '',
  unit: '',
  from: '',
  to: '',
  offset: 0,
  limit: 20,
};
export interface ForecastPort {
  summary(signal: AbortSignal): Promise<ForecastSummary>;
  watches(filter: ForecastWatchFilter, signal: AbortSignal): Promise<ForecastWatchPage>;
  forecasts(filter: ForecastFilter, signal: AbortSignal): Promise<ForecastPage>;
  detail(id: string, signal: AbortSignal): Promise<ForecastDetail>;
  utility(filter: ForecastUtilityFilter, signal: AbortSignal): Promise<ForecastUtility>;
}
export interface ForecastWatchRevision {
  watch: ForecastWatch;
  revision: number;
}
export interface ForecastWatchPage {
  items: ForecastWatchRevision[];
  total: number;
  offset: number;
  limit: number;
  generatedAt: string;
}
export interface ForecastWatchFilter {
  areaId?: string;
  from?: string;
  to?: string;
  offset: number;
  limit: number;
  forecastId?: string;
}
